import { useEffect } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { YearPicker, Loader } from 'qampo-ui-library';
import { useEconomics } from '../hooks/useEconomics';
import { useFetchPermissions } from '../hooks/usePermissions';
import { useAppSelector } from '../store/hooks';
import { selectCanWrite } from '../store/permissions/permissionsSlice';
import { PeriodSelector } from '../components/Economics/PeriodSelector';
import { EconomicsTree } from '../components/Economics/EconomicsTree';
import { Button } from '../components/UI/base/Button';
import { formatDkk } from '../utils/currencyUtils';
import styles from './EconomicsPage.module.css';

export const EconomicsPage = () => {
  const { t, i18n } = useTranslation();
  const { locationId } = useParams<{ locationId: string }>();
  useFetchPermissions(locationId);
  const canWrite = useAppSelector(selectCanWrite);

  const {
    selectedYear,
    setSelectedYear,
    locationName,
    loading,
    saving,
    weeks,
    fromWeek,
    toWeek,
    setPeriod,
    tree,
    isDirty,
    toggleLeaf,
    toggleKeys,
    setOverride,
    save,
    discard
  } = useEconomics(locationId);

  // Warn before a browser navigation / refresh while economics edits are unsaved.
  useEffect(() => {
    if (!isDirty) return;
    const handler = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  return (
    <main className={styles.page}>
      <h1 className={styles.heading}>
        {t('nav-economics')}{' '}
        <div className={styles.yearPicker}>
          <YearPicker
            value={selectedYear}
            onChange={isDirty ? () => {} : setSelectedYear}
            styles={isDirty ? { root: { pointerEvents: 'none', opacity: 0.6 } } : undefined}
            theme={{
              inputBackground: 'transparent',
              popupBackground: 'var(--color-surface-primary)',
              yearSelectedBackground: 'var(--color-interaction-primary)',
              yearSelectedBorderRadius: '15px'
            }}
          />
        </div>
      </h1>
      {locationName && (
        <div className={styles.subHeading}>
          <p className={styles.locationName}>{locationName}</p>
        </div>
      )}
      <div className={styles.body}>
        {loading ? (
          <div className={styles.loading}>
            <Loader />
          </div>
        ) : (
          <>
            <div className={styles.toolbar}>
              <PeriodSelector
                weeks={weeks}
                fromWeek={fromWeek}
                toWeek={toWeek}
                onChange={setPeriod}
                disabled={saving}
              />
              <div className={styles.summary}>
                <div className={styles.summaryBlock}>
                  <span className={styles.summaryValue}>{tree.completedCount}</span>
                  <span className={styles.summaryLabel}>{t('economics-completed-count')}</span>
                </div>
                <div className={styles.summaryBlock}>
                  <span className={styles.summaryValue}>{formatDkk(tree.grandTotal, i18n.language)}</span>
                  <span className={styles.summaryLabel}>{t('economics-grand-total')}</span>
                </div>
                {canWrite && (
                  <div className={styles.actions}>
                    <Button type="secondary" onClick={discard} disabled={!isDirty || saving}>
                      {t('economics-discard')}
                    </Button>
                    <Button type="primary" onClick={save} disabled={!isDirty || saving}>
                      {t('economics-save')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <EconomicsTree
              tree={tree}
              canWrite={canWrite}
              onToggleKeys={toggleKeys}
              onToggleLeaf={toggleLeaf}
              onOverrideChange={setOverride}
            />
          </>
        )}
      </div>
    </main>
  );
};
